import { ProofRequest } from '@snapptinc/fraud-platform';
import axios from 'axios';

import { proofApi } from 'src/api';
import { defaultInstance } from 'src/api/instance';
import { configVars } from 'src/helpers';
import { IProof } from 'src/ts/interfaces';
import { eligibleDocuments } from '../incomeVerification';
import { VerificationData, VerificationFormData } from './ts/interfaces/OfferLetterVerification';

const api = {
  getProofsByEntryId(entryId: string) {
    return proofApi.getProofs(`entry_id=${entryId}`, `id:desc`);
  },

  getIncomeVerificationProofsByEntryId(entryId: string) {
    const eligibleDocumentsQueryString = eligibleDocuments.join('|');
    return proofApi.getProofs(
      `entry_id=${entryId},result=CLEAN,type@>${eligibleDocumentsQueryString}`,
      `id:desc`
    );
  },
  getProofById(proofId: string) {
    return proofApi.getProof(proofId);
  },
  updateProof(proofId: string, proof: Partial<IProof>) {
    return proofApi.updateProof(proofId, { proof } as unknown as ProofRequest);
  },

  submitVerification: async (data: VerificationFormData): Promise<void> => {
    await defaultInstance.post(`${configVars.server_url}/v2/offer_letter/verifications`, data);
  },

  submitInsufficient: async (
    data: { proof_id: string; reason_codes: string[] } & Partial<
      Omit<VerificationFormData, 'proof_id'>
    >
  ): Promise<void> => {
    await defaultInstance.post(`${configVars.server_url}/v2/offer_letter/insufficient`, data);
  },

  // ITL-2769: auto-mark an OL as INSUFFICIENT without analyst content review.
  // Used by the FE's blocked_edited auto-rule when a non-OL proof on the
  // entry is marked EDITED. Distinct from submitInsufficient — does NOT
  // create a Verification row server-side.
  markUnverified: async (proofId: string): Promise<void> => {
    await defaultInstance.post(`${configVars.server_url}/v2/offer_letter/mark_unverified`, {
      proof_id: proofId
    });
  },

  // ITL-2809 Phase 2: leasing-agent on-demand SF case creation. Hits the
  // new `EmploymentVerificationController` which delegates to the
  // existing `SalesforceCaseCreator`. Idempotent — repeat clicks are safe
  // (the backend short-circuits if `salesforce_case_id` is already set).
  // Direct axios call following the established pattern in this file; no
  // new Redux slice, no thunk.
  requestEmploymentVerification: async (entryId: string): Promise<void> => {
    await defaultInstance.post(
      `${configVars.server_url}/v2/offer_letter/employment_verifications`,
      { entry_id: entryId }
    );
  },

  getVerification: async (proofId: string): Promise<VerificationData | null> => {
    try {
      const response = await defaultInstance.get(
        `${configVars.server_url}/v2/offer_letter/verifications/${proofId}`
      );
      return response.data ?? null;
    } catch (error: unknown) {
      // 404 = no AI results yet, not an error
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }
};

export default api;
